/**
 * Centralized z-index scale for the design system.
 *
 * The scale is exposed as **two parallel projections of the same numbers**, because two consumers
 * with opposite constraints both need it:
 *
 * 1. {@link Z_INDEX} — each value is a **complete, literal Tailwind class string** (e.g. `"z-[1020]"`).
 *    Keeping the full class as a static literal is what lets Tailwind's scanner pick it up — the class
 *    name is emitted because it appears verbatim in this scanned source file. Use this for **fixed,
 *    non-nested** placements where the layer's absolute rank is correct on its own.
 *    Never build these at runtime (`` `z-[${n}]` ``): an interpolated string is invisible to the
 *    scanner and produces no CSS.
 *
 * 2. {@link Z_INDEX_VALUE} — the same layers as **raw numbers**. Use this when the z-index must be
 *    **computed** (e.g. `base + offset` for a nested overlay) rather than fixed. A computed number
 *    can't be a scanned Tailwind class, so it is applied via an inline `style={{ "--z": n }}` CSS
 *    variable that the single static class `z-[var(--z)]` reads. See {@link useOverlayZIndex} in
 *    `overlay-z-index.tsx` for the mechanism and the reasoning behind it.
 *
 * ⚠️ The two maps MUST stay in sync: the number in {@link Z_INDEX_VALUE} must equal the number
 * inside the corresponding {@link Z_INDEX} class string. They are written out longhand (rather than
 * one derived from the other) specifically so the class strings remain static literals for the
 * scanner — deriving the strings from the numbers would reintroduce interpolation. If you change a
 * layer's value, change it in BOTH maps.
 *
 * Seeded from @tellescope/ui's layering; subject to review/consolidation as components migrate in.
 * Do not delete {@link Z_INDEX_VALUE} or the `--z` inline-style pattern during a token/style audit:
 * they are load-bearing for nesting-aware overlay stacking, not stray hardcoded values.
 */
export const Z_INDEX = {
  base: "z-[1]",
  raised: "z-[10]",
  sidebar: "z-[30]",
  header: "z-[35]",
  tableHeader: "z-[40]",
  dropdown: "z-[1000]",
  overlay: "z-[2000]",
  sheet: "z-[2020]",
  modalOverlay: "z-[2030]",
  modal: "z-[2040]",
  alertDialog: "z-[2060]",
  floatingWindow: "z-[2500]",
  popover: "z-[2600]",
  tooltip: "z-[2700]",
  toast: "z-[3000]",
} as const;

/**
 * Numeric projection of {@link Z_INDEX} — the same layers, as raw numbers, for **computed** z-index.
 *
 * Each value MUST equal the number embedded in the matching {@link Z_INDEX} class string (see the
 * sync warning on {@link Z_INDEX}). Consumed by {@link useOverlayZIndex}, which adds a layer's value
 * to an inherited base so a nested overlay always stacks above the overlay that contains it.
 */
export const Z_INDEX_VALUE = {
  base: 1,
  raised: 10,
  sidebar: 30,
  header: 35,
  tableHeader: 40,
  dropdown: 1000,
  overlay: 2000,
  sheet: 2020,
  modalOverlay: 2030,
  modal: 2040,
  alertDialog: 2060,
  floatingWindow: 2500,
  popover: 2600,
  tooltip: 2700,
  toast: 3000,
} as const;

export type ZIndexLayer = keyof typeof Z_INDEX;
